import { useEffect } from "react";
import { useForm, useWatch, type Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import type { ObjectSchema } from "yup";

import Stack from "@mui/material/Stack";

import { InputLabelHeight } from "@covertree/types";
import {
  Button,
  FormCurrencyField,
  FormSelectField,
  FormTextField,
} from "@covertree/ui";

import type {
  ExtraCoverageItem,
  ExtraCoverageItemField,
} from "./extraCoverages";

const emptyItem = (fields: readonly ExtraCoverageItemField[]) =>
  Object.fromEntries(fields.map(({ name }) => [name, ""])) as ExtraCoverageItem;

interface ItemFormProps {
  fields: readonly ExtraCoverageItemField[];
  validation?: ObjectSchema<ExtraCoverageItem>;
  item?: ExtraCoverageItem;
  showErrors?: boolean;
  onSave: (item: ExtraCoverageItem) => void;
  onCancel: () => void;
}

/**
 * Inline add/edit form for a single sub-item. Deliberately holds its own form
 * state: the item is only merged into the coverage value on save, so Cancel
 * needs no cleanup and the premium is never touched while editing.
 */
export function ExtraCoverageItemForm({
  fields,
  validation,
  item,
  showErrors,
  onSave,
  onCancel,
}: Readonly<ItemFormProps>) {
  const {
    control,
    watch,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ExtraCoverageItem>({
    defaultValues: item ?? emptyItem(fields),
    resolver: validation
      ? (yupResolver(validation) as Resolver<ExtraCoverageItem>)
      : undefined,
    mode: "onChange",
  });
  const values = useWatch({ control });

  // add highlight or remove it once the error disappears (e.g. Save draft resets the outer form).
  useEffect(() => {
    fields.forEach(({ name }) => {
      if (`${getValues(name) ?? ""}`.trim() !== "") return;

      if (showErrors) setError(name, { type: "required" });
      else clearErrors(name);
    });
  }, [showErrors, fields, getValues, setError, clearErrors]);

  const isComplete =
    fields.every(({ name }) => `${values[name] ?? ""}`.trim() !== "") &&
    Object.keys(errors).length === 0;

  // item carries its locator field (`locator` or `fieldGroupLocator`, per
  // coverage), which is not an editable field but is what marks the item as an
  // update rather than delete + add on the backend.
  const save = () =>
    onSave({
      ...item,
      ...(Object.fromEntries(
        fields.map(({ name, type }) => [
          name,
          type === "currency" ? Number(values[name]) : `${values[name]}`.trim(),
        ])
      ) as ExtraCoverageItem),
    });

  return (
    <Stack
      gap={2}
      p={3}
      mt={1}
      width={1}
      border="1px solid"
      borderColor="action.disabled"
      borderRadius={2}>
      {fields.map(({ name, label, type, options }) => {
        const props = {
          id: `${name}-field`,
          dataTestId: `${name}-field`,
          name,
          control,
          label,
          labelHeight: InputLabelHeight.SMALL,
        };
        if (type === "currency") {
          return <FormCurrencyField key={name} {...props} />;
        }
        if (type === "select") {
          return (
            <FormSelectField
              key={name}
              {...props}
              watch={watch}
              options={options ?? []}
            />
          );
        }

        // Transparent by default; match the white select/currency fields.
        return (
          <FormTextField
            key={name}
            {...props}
            sx={{ backgroundColor: "background.default" }}
          />
        );
      })}

      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button size="small" disabled={!isComplete} onClick={save}>
          {item ? "Save" : "Add Item"}
        </Button>
        <Button variant="outlined" size="small" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}
